/**
 * #1227 — a Polymarket US position as My Trades rows, in the K/P shape: the same OPEN / SELL /
 * SETTLED split and the same formulas as `buildPolyTradeCardVMs`, over the relay's exact fold
 * totals (`myTradesClient.ts`, settledPositions.ts) rather than numbers re-derived from the 200
 * newest fills. Money and quantities stay exact decimal text beside the numeric fields (the
 * #1226 convention); a figure the evidence cannot establish is null and renders "—", the
 * evidence reason in the hover of the money dashes and of the card, never in a cell.
 */

import type { TradeAction, TradeCardVM } from '../../components/cards/TradeCard';
import type { UsMyTradePosition, UsPositionEvidence } from '../../lib/polymarketUs/myTradesClient';
import { formatUsDecimal, formatUsUsd } from '../../lib/polymarketUs/format';
import { usMarketLabel } from '../../lib/polymarketUs/marketLabel';
import { signedDecimalUnits } from '../../lib/polymarketUs/ticketModel';

const SCALE = 10n ** 12n;

/** Why a row's figures are dashes; the hover carries it, the cells stay "—". */
export const US_EVIDENCE_TITLE: Record<UsPositionEvidence, string | null> = {
  complete: null,
  fee_incomplete: 'a fee is missing from the venue evidence',
  orphan_sell: 'a sale exceeds the recorded holding',
  cost_basis_unknown: 'the cost basis cannot be established from the recorded fills',
  payout_unverified: 'P&L pending verified payout',
};

const units = (text: string | null): bigint | null => (text === null || text === '' ? null : signedDecimalUnits(text));

/** Exact 12-place units → signed decimal text, trailing zeros dropped. */
function text(u: bigint): string {
  const negative = u < 0n, abs = negative ? -u : u;
  const fraction = (abs % SCALE).toString().padStart(12, '0').replace(/0+$/, '');
  const body = fraction ? `${abs / SCALE}.${fraction}` : String(abs / SCALE);
  return negative ? `-${body}` : body;
}

/** `part` of `total` as a 12-place amount (truncated toward zero, as the relay allocates fees). */
const share = (amount: bigint, part: bigint, total: bigint) => (total === 0n ? 0n : (amount * part) / total);
/** A per-contract price in cents (the K/P `entryCents`), from an amount over a quantity. */
const cents = (amount: bigint, quantity: bigint) => (quantity === 0n ? null : (Number((amount * SCALE) / quantity) / 1e12) * 100);

interface Money { feesUsd: bigint | null; totalInUsd: bigint | null; cashOutUsd: bigint | null; netPnlUsd: bigint | null }

export function buildUsTradeVMs(p: UsMyTradePosition): TradeCardVM[] {
  const name = usMarketLabel({ marketSlug: p.marketSlug, marketTitle: p.marketTitle });
  const held = units(p.netQuantity), cost = units(p.costBasisUsd), trading = units(p.tradingRealizedUsd);
  const bought = units(p.boughtQuantity), sold = units(p.soldQuantity), proceeds = units(p.sellProceedsUsd);
  const buyFees = units(p.buyFeesUsd), sellFees = units(p.sellFeesUsd);
  const orphan = p.evidence === 'orphan_sell';
  const dashTitle = US_EVIDENCE_TITLE[p.evidence];
  const at = (iso: string | null) => (iso === null ? 0 : Date.parse(iso));
  // A sold share of the buy fees, as K/P's pro-rata allocation (truncated, as the relay's).
  const soldFeeShare = !orphan && sold !== null && sold > 0n && buyFees !== null && bought !== null
    ? share(buyFees, sold, bought) : null;
  // A NO fill's price is 1 − YES (fillHistory.ts), so a NO position's averages are provisional.
  const provisional = p.outcome === 'no';

  const row = (action: TradeAction, qty: bigint | null, entryCents: number | null, exitCents: number | null,
    m: Money, closedAtMs: number): TradeCardVM => {
    // An OPEN row has no exit, cash out or P&L by nature; only a dash the evidence caused takes
    // the reason, so a fully known row carries none.
    const realized = action !== 'OPEN';
    const evidenceDash = qty === null || entryCents === null || m.feesUsd === null || m.totalInUsd === null
      || (realized && (exitCents === null || m.cashOutUsd === null || m.netPnlUsd === null));
    return {
    key: `us-${p.accountId}|${p.marketSlug}|${p.outcome}|${action}`,
    venue: 'polymarket_us',
    ticker: p.marketSlug,
    // The K/P trade-row shape: the matchup as the title (table cell and card), sport · date · slug
    // tail beneath on the card, as a Kalshi row ends with its ticker tail; the slug is the hover.
    title: name.title,
    subtitle: [name.sport, name.date, name.tail].filter(Boolean).join(' · '),
    eventStartLabel: null,
    side: p.outcome,
    team: null,
    action,
    qty: qty === null ? 0 : Number(text(qty)),
    entryCents,
    exitCents,
    feesDollars: m.feesUsd === null ? null : Number(text(m.feesUsd)),
    totalInDollars: m.totalInUsd === null ? null : Number(text(m.totalInUsd)),
    cashOutDollars: m.cashOutUsd === null ? null : Number(text(m.cashOutUsd)),
    netPnlDollars: m.netPnlUsd === null ? null : Number(text(m.netPnlUsd)),
    closedAtMs,
    isRejected: false,
    rejectedReason: null,
    us: {
      qtyText: qty === null ? '—' : formatUsDecimal(text(qty)),
      feesText: m.feesUsd === null ? null : formatUsUsd(text(m.feesUsd)),
      totalInText: m.totalInUsd === null ? null : formatUsUsd(text(m.totalInUsd)),
      cashOutText: m.cashOutUsd === null ? null : formatUsUsd(text(m.cashOutUsd)),
      netPnlText: m.netPnlUsd === null ? null : text(m.netPnlUsd),
      // The settlement exit is the 1/0 payout, not a fill price.
      entryProvisional: provisional && entryCents !== null,
      exitProvisional: provisional && exitCents !== null && action === 'SELL',
      dashTitle: evidenceDash ? dashTitle : null,
      pending: !realized || m.netPnlUsd !== null ? null
        : action === 'SETTLED' && p.evidence === 'payout_unverified' ? 'payout' : 'incomplete',
    },
    };
  };

  const vms: TradeCardVM[] = [];
  // Still held: the open share of the buy fees, as K/P's pro-rata `buyFeeOpen`.
  if (p.status === 'open' && (held === null || held > 0n)) {
    const fees = held !== null && buyFees !== null && bought !== null ? share(buyFees, held, bought) : null;
    vms.push(row('OPEN', held, cost !== null && held !== null ? cents(cost, held) : null, null,
      { feesUsd: fees, totalInUsd: cost !== null && fees !== null ? cost + fees : null, cashOutUsd: null, netPnlUsd: null },
      at(p.lastFillAt)));
  }
  // Sold: entry is the matched cost (proceeds − trading P&L), as K/P's `matchedCost`. A closed
  // position always gets this row, even when its fold is unknown (all dashes), so it never disappears.
  // An orphan sale (more sold than the recorded buys held at the time) is a sale even when the
  // relay could not send the quantities: its row stands, all dashes and pending, never dropped.
  if (p.hasRecordedSale || (sold !== null && sold > 0n) || p.status === 'closed' || (orphan && sold === null)) {
    const matchedCost = !orphan && proceeds !== null && trading !== null ? proceeds - trading : null;
    const buyFeeMatched = soldFeeShare;
    const totalIn = matchedCost !== null && buyFeeMatched !== null ? matchedCost + buyFeeMatched : null;
    const cashOut = proceeds !== null && sellFees !== null ? proceeds - sellFees : null;
    // Short of a settlement, every realized fee is this row's (the relay's realizedFeesUsd: the sold
    // share of the buy fees plus every sell fee), so the relay's own figure stands, also when only
    // the cost is unknown. Without the quantities the relay's share is taken against a holding its
    // fills do not explain, so it is a dash. A settled position splits them with its SETTLED row.
    const fees = orphan || sold === null ? null : p.status !== 'settled' ? units(p.realizedFeesUsd)
      : buyFeeMatched !== null && sellFees !== null ? buyFeeMatched + sellFees : null;
    vms.push(row('SELL', sold, matchedCost !== null && sold !== null ? cents(matchedCost, sold) : null,
      proceeds !== null && sold !== null ? cents(proceeds, sold) : null,
      { feesUsd: fees, totalInUsd: totalIn,
        cashOutUsd: cashOut, netPnlUsd: totalIn !== null && cashOut !== null ? cashOut - totalIn : null },
      at(p.lastFillAt)));
  }
  // Settled: the relay pays a 1/0 result only (settledPositions.ts), so settlement P&L + cost basis
  // is 0 or the whole quantity; reading which one keeps a half-unit rounding of the two out of it.
  if (p.status === 'settled') {
    const settlement = units(p.settlementPnlUsd);
    // The buy fees left after the SELL row's share, so the two rows split them exactly.
    const fees = held === null || buyFees === null || bought === null ? null
      : soldFeeShare !== null ? buyFees - soldFeeShare : share(buyFees, held, bought);
    const payout = settlement === null || cost === null || held === null ? null
      : 2n * (settlement + cost) > held ? held : 0n;
    const totalIn = cost !== null && fees !== null ? cost + fees : null;
    vms.push(row('SETTLED', held, cost !== null && held !== null ? cents(cost, held) : null,
      payout !== null && held !== null ? cents(payout, held) : null,
      { feesUsd: fees, totalInUsd: totalIn, cashOutUsd: payout, netPnlUsd: totalIn !== null && payout !== null ? payout - totalIn : null },
      at(p.settledAt ?? p.lastFillAt)));
  }
  return vms;
}
